import * as React from 'react';
import { cloneDeep } from 'lodash';

import { Checkbox, Button, DateInput, Select, Tooltip } from 'src/components';
import { defaultFilters } from '../services/filters';
import {
  IFiltersProps,
  IFiltersState,
  IParameterValue,
  IParameter,
} from '../types';
import { primaryFactory } from 'src/services/themeColors';

const SELECT_THRESHOLD = 7;
const ParagraphPrimary = primaryFactory('p');

class Filters extends React.Component<IFiltersProps, IFiltersState> {
  today = defaultFilters.dateTo;
  state: IFiltersState = {
    showExtendedParameters: false,
  };

  onDateChange = (field: 'dateFrom' | 'dateTo') => (date: Date) => {
    this.props.onChange({ [field]: date }, true);
  };

  toggleCheckbox = (field: 'showArchive' | 'myLinks') => () => {
    this.props.onChange({ [field]: !this.props.filters[field] }, true);
  };

  setParameters =
    (name: string) => (value: IParameterValue | IParameterValue[] | null) => {
      if (Array.isArray(value) || value === null) {
        const values = value || [];
        const parameters = cloneDeep(this.props.filters.parameters);
        const existingParam = parameters.findIndex(
          (parameter) => parameter.name === name
        );
        if (existingParam !== -1) {
          parameters[existingParam].values = values;
        } else {
          parameters.push({
            name,
            values,
          });
        }
        this.props.onChange({ parameters }, true);
        return;
      }
      this.toggleParameter(name, value)();
    };

  toggleParameter = (name: string, value: IParameterValue) => () => {
    const parameters = cloneDeep(this.props.filters.parameters);
    const existingParam = parameters.findIndex((p) => p.name === name);
    if (existingParam !== -1) {
      const existingValue = parameters[existingParam].values.findIndex(
        (p) => p.value === value.value
      );
      if (existingValue !== -1) {
        parameters[existingParam].values = parameters[
          existingParam
        ].values.filter((_, i) => i !== existingValue);
      } else {
        parameters[existingParam].values.push(value);
      }
    } else {
      parameters.push({
        name,
        values: [value],
      });
    }
    this.props.onChange({ parameters }, true);
  };

  submit = () => {
    this.setState({ showExtendedParameters: false });
    this.props.onSubmit();
  };

  toggleExtendedParameters = () => {
    this.setState((state) => ({
      showExtendedParameters: !state.showExtendedParameters,
    }));
  };

  renderSelect = (parameter: IParameter) => {
    const existingParameter = this.props.filters.parameters.find(
      (p) => p.name === parameter.name
    );
    return (
      <Select
        isMulti={true}
        secondary={true}
        options={parameter.values}
        onChange={this.setParameters(parameter.name)}
        value={existingParameter ? existingParameter.values : []}
      />
    );
  };

  renderInput = (parameter: IParameter) => {
    if (parameter.values.length > SELECT_THRESHOLD) {
      return this.renderSelect(parameter);
    } else {
      return parameter.values.map((el) => (
        <Checkbox
          key={el.value}
          secondary={true}
          value={el.value}
          name={el.value}
          className="my-5"
          onChange={this.toggleParameter(parameter.name, el)}
          checked={
            this.props.filters.parameters.findIndex(
              (p) => p.values.findIndex((v) => v.value === el.value) !== -1
            ) !== -1
          }
        >
          {el.label}
        </Checkbox>
      ));
    }
  };

  render() {
    const { showExtendedParameters } = this.state;
    const {
      filters: { dateFrom, dateTo, showArchive, myLinks },
      isFiltering,
      isLoading,
      parameters,
      extendedParameters,
      myLinksOnly,
    } = this.props;
    return (
      <>
        <div className="relative z-50 mr-4 mb-16 w-64">
          <ParagraphPrimary className="mb-4">Created on</ParagraphPrimary>
          <div className="my-2 flex items-center">
            <span className="inline-block w-12">From</span>
            <DateInput
              selected={dateFrom}
              onChange={this.onDateChange('dateFrom')}
              maxDate={dateTo}
              secondary={true}
              small={true}
              className="flex-1 py-2"
            />
          </div>
          <div className="my-2 flex items-center">
            <span className="inline-block w-12">To</span>
            <DateInput
              selected={dateTo}
              onChange={this.onDateChange('dateTo')}
              minDate={dateFrom}
              maxDate={this.today}
              secondary={true}
              small={true}
              className="flex-1 py-2"
            />
          </div>
          <Checkbox
            secondary={true}
            name="archived"
            className="my-5"
            onChange={this.toggleCheckbox('showArchive')}
            checked={showArchive}
          >
            Archived links
          </Checkbox>
          <Checkbox
            secondary={true}
            name="myLinks"
            className="my-5"
            onChange={this.toggleCheckbox('myLinks')}
            checked={myLinks}
            disabled={myLinksOnly}
          >
            My links
          </Checkbox>
          {isLoading ? (
            <div className="loader bg-grey rounded mb-10 flex-1 h-32" />
          ) : (
            <>
              {parameters.map((parameter) => (
                <div key={parameter.name}>
                  <hr className="border-b border-blue my-5 opacity-25" />
                  <ParagraphPrimary className="mb-3">
                    {parameter.name}
                  </ParagraphPrimary>
                  {this.renderInput(parameter)}
                </div>
              ))}
            </>
          )}

          <div className="sticky flex pin-b pin-l bg-white-transparent pt-3 pb-4 mt-4 text-center">
            <Button
              onClick={this.props.onReset}
              className="flex-1 mr-1 px-4"
              primary={true}
              size="small"
            >
              Reset
            </Button>
            <Button
              loading={isFiltering}
              loadingLabel="Filtering"
              onClick={this.submit}
              className="flex-1 mx-1 px-4"
              size="small"
            >
              Filter
            </Button>
            <Tooltip text="All parameters" secondary={true} position="top">
              <Button
                disabled={isLoading || extendedParameters.length === 0}
                onClick={this.toggleExtendedParameters}
                className="ml-1 w-12"
                size="small"
                neutral={true}
              >
                {showExtendedParameters ? '-' : '+'}
              </Button>
            </Tooltip>
          </div>
        </div>
        {showExtendedParameters && (
          <div className={'extended-parameters'}>
            <div className="bg-white min-h-screen flex flex-wrap content-start ml-80 pr-8 pt-4 pb-12">
              {extendedParameters.map((parameter) => (
                <div key={parameter.name} className="w-1/3 pl-3">
                  <hr className="border-b border-blue my-5 opacity-25" />
                  <ParagraphPrimary className="mb-3">
                    {parameter.name}
                  </ParagraphPrimary>
                  {this.renderSelect(parameter)}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }
}

export default Filters;



// WEBPACK FOOTER //
// ./src/views/Links/components/Filters.tsx